import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, UserMinusIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Card, Container, EmptyState, Loading, PageHeader, Tag } from "../../ui";
import { errorMessageOf, problemOf, scholarshipsApi } from "../../../api/scholarships";
import ApplicationContents from "../review/ApplicationContents";
import ReviewHistory, { RevertNote, reviewHistoryKey, useReviewHistory } from "../review/ReviewHistory";
import { evaluationStateOf, formatDateTime, fullName } from "../review/model";
import { EVALUATOR_KEY, MARKING_NOT_OPEN } from "./EvaluatorPages";
import EligibilityFlag from "../review/EligibilityFlag";
import { ConsentCard, RemoveApplicantModal } from "./Consent";
import { MarksCard, hasInvalidMarks, marksBodyOf, marksOf, unmarkedOf } from "./Marks";

/**
 * Journey step 9: an evaluator marks one application against its programme's rubric. Marks are
 * saved as a draft until submitted; a submitted evaluation is final unless HEC reopens it.
 */
export default function MarkingPage() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const key = [...EVALUATOR_KEY, "application", applicationId];
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: key,
    retry: (count, err) => !["consent-required", MARKING_NOT_OPEN].includes(problemOf(err)?.code) && problemOf(err)?.status !== 404 && count < 2,
    queryFn: () => scholarshipsApi.get(`evaluator/applications/${applicationId}`),
  });
  const application = data?.data;
  // The evaluator's own rounds, and the Admin's comment if their marks were sent back
  const history = useReviewHistory("evaluator", applicationId, { enabled: Boolean(application) });
  const [marks, setMarks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState([]);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (application) setMarks(marksOf(application.myEvaluation));
  }, [application]);

  const problem = isError && problemOf(error);
  if (isPending) return <Loading label="Loading the application…" />;
  if (problem?.code === "consent-required")
    return (
      <Container className="flex flex-col gap-6 py-6">
        <PageHeader title="Before you start" />
        <ConsentCard scholarshipId={problem.body.scholarshipId} onConsented={refetch} />
      </Container>
    );
  if (problem?.code === MARKING_NOT_OPEN) return <EmptyState title="Marking has not started" description="Marking starts once applications close." />;
  if (isError || !marks) return <EmptyState title="This application is not available to you" description="It may not be assigned to you, or you removed this applicant from your list." onRefresh={refetch} />;

  const evaluation = application.myEvaluation;
  const submitted = evaluation.state === "SUBMITTED";
  const rubric = application.rubric;
  const changed = JSON.stringify(marks) !== JSON.stringify(marksOf(evaluation));
  const etag = () => queryClient.getQueryData(key)?.etag ?? `"${evaluation.version}"`;

  const setMark = (criterionId, patch) => {
    setMissing((prev) => prev.filter((id) => id !== criterionId));
    setMarks((prev) => ({ ...prev, [criterionId]: { marks: "", comment: "", ...prev[criterionId], ...patch } }));
  };

  // The evaluation is a separate resource; keep the application's copy in step with it
  const keep = (result) => queryClient.setQueryData(key, { data: { ...application, myEvaluation: result.data }, etag: result.etag ?? `"${result.data.version}"` });

  const saveDraft = async ({ quiet = false } = {}) => {
    if (hasInvalidMarks(rubric, marks)) {
      toast.error("Marks must be between 0 and 100");
      return false;
    }
    setBusy(true);
    try {
      keep(await scholarshipsApi.put(`evaluator/applications/${applicationId}/evaluation`, marksBodyOf(rubric, marks), { ifMatch: etag() }));
      if (!quiet) toast.success("Marks saved as a draft");
      return true;
    } catch (err) {
      console.error(`Failed to save marks for application ${applicationId}:`, err);
      toast.error(errorMessageOf(err, "Failed to save your marks. Please try again."));
      if (problemOf(err)?.code === "stale-version") refetch();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (changed && !(await saveDraft({ quiet: true }))) return;
    setBusy(true);
    try {
      keep(await scholarshipsApi.post(`evaluator/applications/${applicationId}/evaluation/submit`, null, { ifMatch: etag() }));
      queryClient.invalidateQueries({ queryKey: [...EVALUATOR_KEY, "applications"] });
      queryClient.invalidateQueries({ queryKey: [...EVALUATOR_KEY, "scholarships"] });
      queryClient.invalidateQueries({ queryKey: reviewHistoryKey("evaluator", applicationId) });
      toast.success("Marks submitted");
    } catch (err) {
      console.error(`Failed to submit marks for application ${applicationId}:`, err);
      setMissing(unmarkedOf(problemOf(err)));
      toast.error(errorMessageOf(err, "Failed to submit your marks. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const state = evaluationStateOf(evaluation.state);
  const rounds = history.data?.rounds ?? [];
  const openRevert = history.data?.openRevert;
  // While re-marking, each criterion shows what the evaluator gave in the round that was sent back
  const previous = !submitted && rounds.length ? rounds.at(-1) : null;

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={`/evaluation/${application.scholarship.id}`} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> Applications to review
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {fullName(application.applicant)}
            <Tag color={state.color}>{state.label}</Tag>
          </span>
        }
        subtitle={`${application.scholarship.name} · submitted ${formatDateTime(application.submittedAt)}`}
        actions={
          !submitted && (
            <Button variant="danger-soft" icon={<UserMinusIcon className="h-4 w-4" />} onClick={() => setRemoving(true)}>
              I have a conflict
            </Button>
          )
        }
      />

      {openRevert && (
        <section aria-labelledby="revert-heading" className="flex flex-col gap-2">
          <h2 id="revert-heading" className="text-lg font-semibold text-slate-900">
            HEC sent your marks back
          </h2>
          <p className="text-sm text-slate-700">Mark this application again with the comment below in mind. Your round {rounds.at(-1)?.roundNo} marks and comments are kept as they were.</p>
          <RevertNote comment={openRevert} heading="Comment from the Scholarship Admin" />
        </section>
      )}
      {history.isError && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Comments could not be loaded, so a comment from HEC may be missing.
          <Button variant="outline" size="sm" onClick={() => history.refetch()}>
            Retry
          </Button>
        </div>
      )}

      <EligibilityFlag eligibility={application.eligibility} />

      <ApplicationContents
        application={application}
        documentPath={(id) => `evaluator/applications/${applicationId}/documents/${id}/download-url`}
        attachmentPath={(id) => `evaluator/applications/${applicationId}/attachments/${id}/download-url`}
      />

      <MarksCard
        rubric={rubric}
        marks={marks}
        evaluation={evaluation}
        changed={changed}
        missing={missing}
        busy={busy}
        previous={previous}
        onChange={setMark}
        onSave={() => saveDraft()}
        onSubmit={submit}
      />

      {rounds.length > 0 && (
        <Card title="Your review history" subtitle="Each submission is kept as it was. Add a correction to a comment if you need to; the original stays.">
          <ReviewHistory applicationId={applicationId} path="evaluator" query={history} canCorrect={(comment) => comment.kind === "MARK"} />
        </Card>
      )}

      <RemoveApplicantModal
        applicant={fullName(application.applicant)}
        applicationId={removing ? applicationId : null}
        onClose={() => setRemoving(false)}
        onRemoved={() => navigate(`/evaluation/${application.scholarship.id}`)}
      />
    </Container>
  );
}
